<script setup lang="ts">
import TagSelectFromType from '~/components/tag/selectFromType.vue'
import TagSelectFromTypeLoopOnParent from '~/components/tag/selectFromTypeLoopOnParent.vue'
import TagSingleRadioSelect from '~/components/tag/singleRadioSelect.vue'
import TagToggleAllAndType from '~/components/tag/toggleAllAndType.vue'
import ToggleContainerTagSelector from '~/components/tag/toggleContainerSelector.vue'
import HelpBlock from '~/components/ui/Cards/HelpBlock.vue'

import { useUserInfluencerTagsStore } from '~/stores/userInfluencerTags'

import tagAvailableParentKeys from '~/assets/json/tagAvailableParentKeys'

import type { NudgeName } from '~/api-core/Curators/CuratorProfileCompletion'
import type {
  AllowedComponent,
  Configuration,
} from '~/components/tag/toggleContainerSelector.vue'
import type Spacing from '~/types/spacing'
import type { TagParentTypeKey } from '~/types/tagParentTypeKey'
import type TagTarget from '~/types/tagTarget'
import type TagTypeKey from '~/types/tagTypeKey'

type Validity = {
  liked: boolean
  neutral: boolean
  hated: boolean
  exclusivity_lyrics_lang: boolean
  exclusivity_country: boolean
  exclusivity_track_age: boolean
}

type SubConfiguration = {
  configurationArray: Configuration[]
  target: TagTarget
  expand: boolean
}

type TagEditData = {
  target: TagTarget
  index: number
  modifiedFields: TagTypeKey[]
}

type Props = {
  expandedIndex: number | number[]
  padding?: Spacing
  exclusivityOnly?: boolean
  genresOnly?: boolean
}
type Emits = {
  'container-opening-done': []
  'container-closing-done': []
  'confirm-modifications': [tagEditData: TagEditData]
  'finalize-selection': [tagEditData: TagEditData]
  'click-outside': [tagEditData: TagEditData]
  'handle-hollow-main-btn-click': [tagEditData: TagEditData]
  'need-scroll-up': []
}
type Slots = {
  default(binding: { index: number; target: TagTarget }): any
  'hollow-main-btn-text'(): any
  'confirm-text'(binding: { index: number }): any
}

defineSlots<Slots>()
const emit = defineEmits<Emits>()
const props = withDefaults(defineProps<Props>(), {
  padding: '6',
  exclusivityOnly: false,
  genresOnly: false,
})

const { t } = useI18n()

const targets: TagTarget[] = props.exclusivityOnly
  ? []
  : ['liked', 'neutral', 'hated']
const typeNames: TagTypeKey[] = ['subgenre', 'mood', 'format', 'artist_kind']
const exclusivityTypes: TagTypeKey[] = ['lyrics_lang', 'country']

const validity = ref<Validity>({
  liked: true,
  neutral: true,
  hated: true,
  exclusivity_lyrics_lang: true,
  exclusivity_country: true,
  exclusivity_track_age: true,
})
const modifiedFields = ref<TagTypeKey[]>([])

const {
  SET_TARGET: SET_TAGS_FOR_TARGET,
  GET_TAGS: GET_INFLUENCER_TAGS,
  GET_DISALLOWED: GET_INFLUENCER_DISALLOWED,
} = useUserInfluencerTagsStore()

const metaConfiguration = computed<SubConfiguration[]>(() => {
  return [
    ...targets.reduce(
      (accumulator: SubConfiguration[], target: TagTarget, index: number) => {
        accumulator.push({
          configurationArray: buildConfigurationArray(target),
          expand: isConfigExpanded(index),
          target,
        } satisfies SubConfiguration)
        return accumulator
      },
      [] as SubConfiguration[],
    ),
    ...(props.genresOnly ? [] : buildExclusivityConfiguration(targets.length)),
  ]
})

function validationShortHand(configuration: Configuration): boolean {
  return configuration.props.modelValue.length > 0
}

function genreValidationShorthand(subConfiguration: SubConfiguration) {
  // 'Liked' is optional
  // Likewise, the 'Others' subsections of each sections are also optional => we only consider the subgenre subsection
  const isOptional = subConfiguration.target === 'neutral'
  if (isOptional) return true
  return subConfiguration.configurationArray[0].props.modelValue.length > 0
}

function buildExclusivityConfiguration(index: number): SubConfiguration[] {
  const output: SubConfiguration[] = []
  if (props.exclusivityOnly) {
    output.push(
      ...exclusivityTypes.map(
        (typeName, exclusivityIdx): SubConfiguration => ({
          target: 'exclusivity',
          expand: isConfigExpanded(index + exclusivityIdx),
          configurationArray: [buildSingleConfig('exclusivity', typeName)],
        }),
      ),
    )
  } else {
    output.push({
      target: 'exclusivity',
      expand: isConfigExpanded(index),
      configurationArray: exclusivityTypes.map(
        (typeName: TagParentTypeKey | TagTypeKey): Configuration => {
          return buildSingleConfig('exclusivity', typeName)
        },
      ) as Configuration[],
    })
  }
  output.push({
    target: 'exclusivity',
    expand: isConfigExpanded(
      index + (props.exclusivityOnly ? exclusivityTypes.length - 1 : 0) + 1,
    ),
    configurationArray: [buildSingleConfig('exclusivity', 'track_age')],
  })
  return output
}

function isConfigExpanded(index: number): boolean {
  if (typeof props.expandedIndex === 'number')
    return props.expandedIndex === index
  else return props.expandedIndex.includes(index)
}

function getId(subConfiguration: SubConfiguration): NudgeName | never {
  const { target, configurationArray } = subConfiguration

  switch (target) {
    case 'liked':
      return 'liked_subgenres'
    case 'hated':
      return 'hated_subgenres'
    case 'exclusivity': {
      const isLyricsOrLang = configurationArray.some(
        (config) => config.props.typeName === 'lyrics_lang',
      )
      return isLyricsOrLang ? 'lyrics_lang_countries' : 'track_age'
    }
  }
  return 'unknown-section' as never
}

function handleClickOutside(index: number, { target }: SubConfiguration) {
  emit('click-outside', { index, target, modifiedFields: modifiedFields.value })
}

function handleConfirmModifications({ index }: { index: number }): void {
  const target: TagTarget = metaConfiguration.value[index].target
  const emitData: TagEditData = {
    target,
    index,
    modifiedFields: modifiedFields.value,
  }
  if (!targetValidation(metaConfiguration.value[index] as SubConfiguration))
    return

  if (
    typeof props.expandedIndex !== 'number' ||
    metaConfiguration.value.length - 1 !== index
  )
    emit('confirm-modifications', emitData)
  else emit('finalize-selection', emitData)
}

function targetValidation(subConfiguration: SubConfiguration): boolean {
  const { target } = subConfiguration
  if (props.genresOnly && ['liked', 'neutral', 'hated'].includes(target)) {
    const isValid = genreValidationShorthand(subConfiguration)
    validity.value[target as 'liked' | 'neutral' | 'hated'] = isValid
    emit('need-scroll-up')
    return isValid
  }
  if (target !== 'exclusivity') return true

  return subConfiguration.configurationArray.every(
    (configuration: Configuration) => {
      switch (configuration.props.typeName) {
        case 'lyrics_lang':
        case 'country':
        case 'track_age': {
          return (validity.value[
            `${target}_${configuration.props.typeName}` as const
          ] = validationShortHand(configuration))
        }
        default:
          return false
      }
    },
  )
}

function buildConfigurationArray(target: TagTarget): Configuration[] {
  return typeNames.map(
    (typeName: TagTypeKey | TagParentTypeKey): Configuration =>
      buildSingleConfig(target, typeName),
  )
}

function buildSingleConfig(
  target: TagTarget,
  typeName: TagParentTypeKey | TagTypeKey,
): Configuration {
  const maxAmount = hasLimit(target, typeName) ? 10 : 0

  return {
    component: defineComponentFromTarget(target, typeName),
    patchFunction: SET_TAGS_FOR_TARGET,
    target,
    subtitle:
      maxAmount > 0
        ? t(`tags.toggleContainerSelector.subtitle.${typeName}`, maxAmount)
        : undefined,
    props: {
      title: defineIfConfigHasTitle(target, typeName)
        ? t(`tags.toggleAllAndType.influencer.title.${typeName}`, { count: 1 })
        : undefined,
      disallowed: GET_INFLUENCER_DISALLOWED(target, typeName),
      maxAmount,
      includeUnderScoreTags: false,
      typeName,
      modelValue: GET_INFLUENCER_TAGS(target, typeName),
      pValidity:
        // @ts-expect-error Can't make it work
        validity.value[target] ??
        // @ts-expect-error Can't make it work
        validity.value[`${target}_${typeName}`] ??
        true,
    },
  } satisfies Configuration
}

function handleChange({
  configuration,
}: {
  configuration: Configuration
}): void {
  const {
    target,
    props: { typeName },
  } = configuration
  if (targets.includes(target)) {
    validity.value[target as keyof Validity] = true
  } else if (exclusivityTypes.includes(typeName)) {
    validity.value[`${target}_${typeName}` as keyof Validity] = true
    if (modifiedFields.value.includes(typeName)) return

    modifiedFields.value.push(typeName)
  }
}

function hasLimit(
  target: TagTarget,
  typeName: TagParentTypeKey | TagTypeKey,
): boolean {
  const targets: readonly TagTarget[] = ['liked', 'neutral']
  const types: readonly (TagParentTypeKey | TagTypeKey)[] = ['subgenre']

  return targets.includes(target) && types.includes(typeName)
}

function defineIfConfigHasTitle(
  target: TagTarget,
  typeName: TagParentTypeKey | TagTypeKey,
): boolean {
  return (
    ['exclusivity'].includes(target) &&
    ['country', 'lyrics_lang'].includes(typeName)
  )
}
function defineComponentFromTarget(
  target: TagTarget,
  typeName: TagTypeKey | TagParentTypeKey,
): AllowedComponent {
  if (target === 'exclusivity') {
    if (typeName !== 'track_age') return TagToggleAllAndType
    else return TagSingleRadioSelect
  } else if (tagAvailableParentKeys.includes(typeName as TagParentTypeKey)) {
    return TagSelectFromTypeLoopOnParent
  } else {
    return TagSelectFromType
  }
}
</script>

<template>
  <div class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-gap-6">
    <ToggleContainerTagSelector
      v-for="(subConfiguration, index) in metaConfiguration"
      :id="getId(subConfiguration)"
      :key="index"
      :configuration-array="subConfiguration.configurationArray"
      :expand="subConfiguration.expand"
      :padding="padding"
      @confirm-modifications="handleConfirmModifications({ ...$event, index })"
      @click-outside="handleClickOutside(index, subConfiguration)"
      @change="handleChange"
      @handle-hollow-main-btn-click="
        emit('handle-hollow-main-btn-click', {
          index,
          target: subConfiguration.target,
          modifiedFields,
        })
      "
      @container-opening-done="emit('container-opening-done')"
      @container-closing-done="emit('container-closing-done')"
    >
      <template #default>
        <slot name="default" :index="index" :target="subConfiguration.target" />
      </template>
      <template #hollow-main-btn-text>
        <slot name="hollow-main-btn-text" />
      </template>
      <template #confirm-text>
        <slot name="confirm-text" :index="index">
          {{
            index === metaConfiguration.length - 1
              ? t('common.nextStep')
              : t('common.next')
          }}
        </slot>
      </template>
    </ToggleContainerTagSelector>
    <HelpBlock
      v-if="genresOnly"
      :link="$t('influencer.profile.genresFAQ.link')"
      :title="$t('influencer.profile.genresFAQ.title')"
      :description="$t('influencer.profile.genresFAQ.subtitle')"
    />
  </div>
</template>
